import { PageBlock } from '@design-system/page-block';
import { Events } from '@phx-husky/auth-service';
import {
  addCustomEventListener,
  removeCustomEventListener,
} from '@phx-husky/custom-event';
import Login, { ForgotPassword } from '@phx-husky/login';
import Profile from '@phx-husky/profile';
import Register from '@phx-husky/register';
import { useLocation } from '@reach/router';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { FC, useEffect } from 'react';

import { useCurrentLang } from '~/hooks/useCurrentLang';
import { AuthQuery } from '~/layout/Auth/models';
import { authLinkMapper } from '~/mappers/authLinks';
import { gtmService } from '~/services/gtm';

import {
  CONFIRMATION,
  FORGOT_PASSWORD,
  LOGIN_FORM,
  PROFILE_FORM,
  REGISTER_FORM,
} from './constants';
import { LoginRegisterFormProps } from './models';

const LoginRegisterForm: FC<LoginRegisterFormProps> = ({
  pageBlockUI,
  componentId,
  variant,
  uid,
}) => {
  const { state: routeState } = useLocation() as {
    state: {
      reconfirmEmail?: string;
      confirmedEmail?: string;
      pageKey?: string;
    };
  };

  const {
    currentLang: { locale: currentLangCode },
  } = useCurrentLang();
  const {
    allContentstackAuthSettings: { nodes: authLinks },
  } = useStaticQuery<AuthQuery>(graphql`
    {
      allContentstackAuthSettings {
        nodes {
          authLinks: auth_links {
            ...AuthLinksFragment
          }
          tags
          publishDetails: publish_details {
            locale
          }
        }
      }
    }
  `);

  const authLinksSettings = authLinks.find(
    ({ tags, publishDetails: { locale } }) =>
      tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
      locale === currentLangCode
  );

  if (!authLinksSettings) {
    throw new Error('Auth links settings not exist');
  }

  const mappedLinks = authLinkMapper(authLinksSettings.authLinks);

  const {
    loginLinkHref,
    profileLinkHref,
    registerLinkHref,
    loginSuccessLinkHref,
    registerSuccessLinkHref,
    passwordSuccessLinkHref,
    forgotPasswordLinkHref,
  } = mappedLinks;

  useEffect(() => {
    const handleSuccessRegistration = () =>
      navigate(registerSuccessLinkHref || '/');
    gtmService.emitFormSubmit(REGISTER_FORM);
    const handleSuccessActionConfirmation = () =>
      navigate(loginSuccessLinkHref || '/');
    gtmService.emitFormSubmit(CONFIRMATION);
    const handleSuccessLogin = () => {
      navigate(profileLinkHref || '/');
      gtmService.emitFormSubmit(LOGIN_FORM);
    };

    addCustomEventListener(Events.onSignIn, handleSuccessLogin);
    addCustomEventListener(
      Events.onSuccessActionConfirmation,
      handleSuccessRegistration
    );
    addCustomEventListener(
      Events.onAccountConfirmation,
      handleSuccessActionConfirmation
    );

    return () => {
      removeCustomEventListener(Events.onSignIn, handleSuccessLogin);
      removeCustomEventListener(
        Events.onSuccessActionConfirmation,
        handleSuccessRegistration
      );
      removeCustomEventListener(
        Events.onAccountConfirmation,
        handleSuccessActionConfirmation
      );
    };
  }, []);

  const formTypeMap = {
    [LOGIN_FORM]: (
      <Login
        isWrapperComponent={false}
        onClickSignUp={() => navigate(registerLinkHref || '/')}
        onClickForgotPassword={() => navigate(forgotPasswordLinkHref || '/')}
        onConfirmationCallback={() => {
          // here we should redirect to /login, but there is a problem
          // that the confirmation screen is also on /login which causes a problem
          // to be fixed in the future
          navigate(0);
        }}
      />
    ),
    [FORGOT_PASSWORD]: (
      <ForgotPassword
        isWrapperComponent={false}
        onClickPasswordChanged={() => navigate(passwordSuccessLinkHref || '/')}
        onClickSignIn={() => navigate(loginLinkHref || '/')}
        onHandleConfirmAccount={(state) =>
          navigate(registerLinkHref || '/', {
            state,
          })
        }
        confirmedEmailReturn={routeState?.confirmedEmail}
      />
    ),
    [REGISTER_FORM]: (
      <Register
        isWrapperComponent={false}
        onClickSignIn={() => navigate(loginLinkHref || '/')}
        confirmAccountWithEmail={routeState?.reconfirmEmail}
        confirmCallback={(state) => {
          navigate(forgotPasswordLinkHref || '/', {
            state,
          });
        }}
      />
    ),
    [PROFILE_FORM]: <Profile />,
  };

  return (
    <PageBlock
      {...pageBlockUI}
      id={componentId}
      className="login-register-form"
      componentUid={uid}
    >
      {formTypeMap[variant]}
    </PageBlock>
  );
};

export default LoginRegisterForm;
